package com.exura.features.storage.data.repository

import com.exura.features.firebase.usecase.GetUserDataPrefixUseCase
import com.exura.features.storage.domain.EntityNotFoundException
import com.exura.features.storage.domain.model.DomainModel
import com.exura.features.storage.domain.model.ModelDto
import com.exura.features.storage.domain.repository.EntityRepository
import dev.gitlive.firebase.firestore.CollectionReference
import dev.gitlive.firebase.firestore.FirebaseFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import kotlinx.serialization.KSerializer
import kotlinx.serialization.modules.SerializersModule


class FirebaseEntityRepository<T: DomainModel<T, D>, D: ModelDto<D>>(
    private val userDataPrefixUseCase: GetUserDataPrefixUseCase,
    private val collectionName: String,
    private val factory: DomainModel.Factory<T, D>,
    private val serializer: KSerializer<D>,
    private val dtoSerializersModule: SerializersModule = SerializersModule {  },
) : EntityRepository<T> {
    override var sourcePrefix: String? = null

    private suspend fun getCollectionPrefix() : CollectionReference = userDataPrefixUseCase().getOrThrow().collection(
        sourcePrefix?.let { pref ->
            "${pref}/${collectionName}"
        } ?:  collectionName
    )

    override suspend fun create(item: T): Result<T> = runCatching {
        val dto = item.toDto()
        val docRef = getCollectionPrefix().add(serializer,dto) { serializersModule = dtoSerializersModule }
        item.withId(docRef.id)
    }

    override suspend fun read(id: String): Result<T> = runCatching {
        val doc = getCollectionPrefix().document(id).get()
        if (!doc.exists) {
            throw EntityNotFoundException(doc.id)
        }
        val dto = doc.data(serializer) { serializersModule = dtoSerializersModule }
        factory.fromDto(id, dto)
    }

    override suspend fun update(id: String, vararg fieldAndValues: Pair<String, Any?>): Result<Unit> = runCatching {
        val docRef = getCollectionPrefix().document(id)
        val doc = docRef.get()

        if (!doc.exists) {
            throw EntityNotFoundException(doc.id)
        }
        docRef.update(fieldAndValues)
    }

    override suspend fun delete(id: String): Result<Unit> = runCatching {
        val docRef = getCollectionPrefix().document(id)
        val doc = docRef.get()

        if (!doc.exists) {
            return@runCatching
        }

        docRef.delete()
    }

    override suspend fun set(id: String, item: T): Result<Unit> = runCatching {
        val docRef = getCollectionPrefix().document(id)

        docRef.set(serializer, item.toDto()) { serializersModule = dtoSerializersModule }
    }

    override suspend fun list(): Flow<List<T>> {
        val coll = getCollectionPrefix()
        return coll.snapshots.map { snapshot ->
            snapshot.documents.mapNotNull { doc ->
                try {
                    val dto = doc.data(serializer) { serializersModule = dtoSerializersModule }
                    factory.fromDto(doc.id, dto)
                } catch (e: Exception) {
                    null
                }
            }
        }
    }
}
